import React from 'react';
import styles from './aboutUs.module.css';

const AboutUs = () => {
    const missionItems = [
        {
            title: 'Auto-mapped BOMs',
            desc: 'Instantly upload and map raw Bill of Materials into verified parts lists, reducing processing times from days to seconds.'
        },
        {
            title: 'Real-time stock & lead times',
            desc: 'Gain instant visibility into global warehouse stock quantities, dynamic pricing, and reliable delivery lead times.'
        },
        {
            title: 'Delivery scheduling',
            desc: 'Align incoming component batches with your manufacturing production runs to optimize working capital.'
        },
        {
            title: 'Global sourcing reach',
            desc: 'Access verified inventories across North America, Europe, and Asia through a single integrated portal.'
        },
        {
            title: 'Liquidate excess inventory',
            desc: 'Consolidate and sell slow-moving, end-of-life, or excess components to our verified buyer network.'
        },
        {
            title: 'Authorized supplier network',
            desc: 'Ensure absolute component authenticity with complete trace documentation from OEM-authorized suppliers.'
        }
    ];

    const coreValues = [
        {
            title: 'Absolute Quality',
            desc: 'Zero tolerance for counterfeit components. Every shipment undergoes rigorous visual inspection and code verification.'
        },
        {
            title: 'Velocity Sourcing',
            desc: 'Empowering purchasing teams by delivering instant quotes, accelerated lead times, and automated logistics clearing.'
        },
        {
            title: 'Complete Transparency',
            desc: 'No hidden handling fees or freight costs. Track order milestones from customs release to your receiving dock.'
        }
    ];

    const teamMembers = [
        {
            name: 'Sourabh Jain',
            role: 'Chief Executive Officer',
            img: '/assets/sourabh_jain.jpg'
        },
        {
            name: 'Sahil Jain',
            role: 'Chief Technology Officer',
            img: '/assets/sahil_jain.jpg'
        },
        {
            name: 'Mahima Jain',
            role: 'Chief Financial Officer',
            img: '/assets/mahima_jain.jpg'
        }
    ];

    return (
        <div className={styles.pageWrapper}>
            {/* Hero Section */}
            <div className={styles.heroSection}>
                <div className={styles.heroImageWrapper}>
                    <img src="assets/electrowiz_about_hero.jpg" alt="Electrowiz Sourcing platform" className={styles.heroImage} />
                    <div className={styles.heroOverlay}></div>
                    <div className={styles.glowOrb1}></div>
                    <div className={styles.glowOrb2}></div>
                    <div className={styles.glowOrb3}></div>
                </div>
                <div className={styles.heroContent}>
                    <h1>About Electrowiz</h1>
                    <p className={styles.heroSubtitle}>Simplifying and modernizing electronic component sourcing across India</p>
                </div>
            </div>

            {/* Stats Bar */}
            <div className={styles.statsSection}>
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <span className={styles.statNum}>Bangalore</span>
                        <span className={styles.statLabel}>Operations Hub</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={styles.statNum}>10M+</span>
                        <span className={styles.statLabel}>Components Tracked</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={styles.statNum}>50+</span>
                        <span className={styles.statLabel}>Global Partners</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={styles.statNum}>99.9%</span>
                        <span className={styles.statLabel}>Sourcing Accuracy</span>
                    </div>
                </div>
            </div>

            {/* Introduction / Story */}
            <div className={styles.storySection}>
                <div className={styles.storyGrid}>
                    <div className={styles.storyTextCol}>
                        <span className={styles.sectionBadge}>Our Mission</span>
                        <h2>The Next-Gen Sourcing Platform</h2>
                        <p>
                            Based in Bangalore—the Silicon Valley and start-up capital of India—Electrowiz was established to bridge the efficiency gap between product development teams and global supply networks.
                        </p>
                        <p>
                            Traditional sourcing involves endless emails, long quote waiting times, and constant uncertainty over component authenticity. We believe hardware innovators deserve digital tools that match the speed of modern software. Our platform helps manufacturers, EMS companies, and technology developers build, schedule, and secure their component bills of materials in record time.
                        </p>
                    </div>
                    <div className={styles.storyVisualCol}>
                        <div className={styles.visualContainer}>
                            <img src="assets/electrowiz_tech_ops.jpg" alt="High tech circuit board illustration" className={styles.visualImage} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Advantages Section */}
            <div className={styles.advantagesSection}>
                <div className={styles.sectionHeader}>
                    <span className={styles.sectionBadge}>Platform Advantages</span>
                    <h2>Why Engineering & Sourcing Teams Trust Us</h2>
                    <p className={styles.sectionSubtext}>We design intelligent tools that eliminate sourcing friction, providing complete visibility from quote to delivery.</p>
                </div>

                <div className={styles.advantagesGrid}>
                    {missionItems.map((item, index) => (
                        <div key={index} className={styles.advantageCard}>
                            <div className={styles.cardHeader}>
                                <div className={styles.cardIconWrapper}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                </div>
                            </div>
                            <h3>{item.title}</h3>
                            <p>{item.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Technology & Operations */}
            <div className={styles.techOpsSection}>
                <div className={styles.techOpsGrid}>
                    <div className={styles.techOpsImageCol}>
                        <img src="assets/tech.png" alt="Technology and Sourcing Process flow" className={styles.techOpsImage} />
                    </div>
                    <div className={styles.techOpsTextCol}>
                        <span className={styles.sectionBadge}>Operations Excellence</span>
                        <h2>Technology & Logistics Integration</h2>
                        <p>
                            Our technology layer connects seamlessly with global inventories, giving our users live access to authorized component databases worldwide. But tech is only half of the equation.
                        </p>
                        <p>
                            Electrowiz manages the complex, low-level mechanics of international trade. From managing customs clearance procedures to arranging secure consolidated shipments, we handle the logistics end-to-end. Our quality control center in Bangalore inspects every arriving batch to guarantee it meets manufacturer spec sheets before reaching your factory floor.
                        </p>
                    </div>
                </div>
            </div>

            {/* Core Values */}
            <div className={styles.valuesSection}>
                <div className={styles.sectionHeader}>
                    <span className={styles.sectionBadge}>Core Values</span>
                    <h2>Our Sourcing Philosophy</h2>
                </div>
                <div className={styles.valuesGrid}>
                    {coreValues.map((value, idx) => (
                        <div key={idx} className={styles.valueCard}>
                            <div className={styles.valueBar}></div>
                            <h3>{value.title}</h3>
                            <p>{value.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Meet the Team */}
            <div className={styles.teamSection}>
                <div className={styles.sectionHeader}>
                    <span className={styles.sectionBadge}>Team</span>
                    <h2>Meet The Team</h2>
                    <p className={styles.sectionSubtext}>The engineers, logistics experts, and sourcing professionals building the future of hardware procurement.</p>
                </div>
                <div className={styles.teamGrid}>
                    {teamMembers.map((member, idx) => (
                        <div key={idx} className={styles.teamMemberCard}>
                            <div className={styles.teamImgWrapper}>
                                <img src={member.img} alt={member.name} className={styles.teamMemberImg} />
                            </div>
                            <h4>{member.name}</h4>
                            <p className={styles.teamMemberRole}>{member.role}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AboutUs;